/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState, useEffect } from 'react';
import { AxiosError } from 'axios';
import { css, StyleSheet } from 'aphrodite';
import { connect } from 'react-redux';
import { useDispatch } from 'react-redux';
import { InjectedFormProps, reduxForm, getFormValues, change, getFormSyncErrors } from 'redux-form';
import { mixins, colors, fonts } from '@blend/consumer-components';

import { State } from '../../../redux';
import { TaskForm } from '../../form/TaskForm';
import { InfoCard } from '../../form/InfoCard/InfoCard';
import { TaskInputs } from '../../form/TaskInputs/TaskInputs';
import { InputGroup } from '../../form/TaskInputs/InputGroup/InputGroup';
import { AttachmentCard } from '../../common/AttachmentCard/AttachmentCard';
import { notaryAPI } from '../../../api/notary';
import { fetchJournalEntry } from '../../../actions/journalActions';
import { AUDIO_VIDEO_STATEMENT_CALIFORNIA_REQUIREMENT } from '../../../shared/constants/journalEntry';
import { dataModelAddressToString } from '../../form/Inputs/AddressInput/utils/addressUtils';
import { AddressInput } from '../../form/Inputs/AddressInput/types';
import { taskFormValuesToEntry } from './utils';
import { Wrapper, Error } from '@components/form/Inputs/InputError';
import { ActionButton } from '@components/ActionButton/ActionButton';
import { Card } from '@components/Card/Card';
import { noop, isEmpty } from '@utils/lodash';

const FORM_NAME = 'task';

const JOURNAL_ERROR_MESSAGE =
  'There was a problem saving the information on this page. ' +
  'Refresh the page and try again, or contact us if you still have trouble.';

interface FormValues {
  notaryLocation?: AddressInput.Address;
  fees?: string;
}

export namespace JournalEntry {
  export interface DispatchProps {}
  export interface OwnProps {
    handleClose: () => void;
  }
  export interface StateProps {
    currentEntry: State['entry']['current'];
    entryTask: State['entry']['entryTask'];
    isLocked: State['entry']['isLocked'];
    formValues: FormValues;
    initialValues: FormValues;
    formErrors: any;
  }
  export type Props = InjectedFormProps<FormValues, StateProps & OwnProps> & StateProps & OwnProps;
}

const styles = StyleSheet.create({
  title: {
    fontSize: 16,
    color: colors.darkerGray,
    fontWeight: 500,
    ...mixins.margin(0, 0, 24, 0),
  },
  header: {
    color: colors.darkerGray,
    fontSize: 14,
    fontWeight: 500,
    ...mixins.margin(24, 0, 10, 0),
  },
  taskWrapper: {
    maxWidth: 520,
    width: '100%',
    ...mixins.padding(24),
    ...fonts.UntitledSans,
  },
  signerInfoCard: {
    ...mixins.margin(0, 0, 24, 0),
  },
  attachmentContainer: {
    marginTop: 10,
  },
  notesContainer: {
    marginTop: 24,
    marginBottom: 24,
  },
  audioVideoStatementContainer: {
    marginTop: 24,
    marginBottom: 24,
  },
  audioVideoStatement: {
    color: colors.catalystLight80,
    fontSize: 12,
    textAlign: 'justify',
    ...mixins.margin(24, 0, 0, 0),
  },
  requiredField: {
    color: '#e02020',
    fontWeight: 'bold',
  },
  notaryLocationInput: {
    marginBottom: 10,
  },
  documentsHeader: {
    color: colors.darkerGray,
    fontSize: 14,
    fontWeight: 500,
    ...mixins.margin(24, 0, 12, 0),
  },
  noDocuments: {
    color: colors.catalystLight80,
    fontSize: 14,
    textAlign: 'justify',
    ...mixins.margin(24, 0, 0, 0),
  },
  saveAndLockButton: {
    marginBottom: 12,
  },
  loadingText: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  noteText: {
    fontWeight: fonts.weight.bold,
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginBottom: 0,
  },
});

export const _JournalEntry: React.FC<JournalEntry.Props> = ({
  currentEntry,
  entryTask,
  isLocked,
  formValues,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const [journalError, setJournalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialFeeValue = entryTask?.appointmentInformation?.find(item => item.primary === 'Fee')?.value || '';

  const [displayedNotaryFee, setDisplayedNotaryFee] = useState<string>('');

  useEffect(() => {
    if (initialFeeValue) {
      setDisplayedNotaryFee(initialFeeValue);
      // Convert string to number (remove $)
      const numericValue = initialFeeValue.replace(/[^0-9.-]+/g, '');

      // Update Redux Form state
      dispatch(change(FORM_NAME, 'fees', numericValue));
    } else if (formValues?.fees) {
      setDisplayedNotaryFee(formValues.fees);
    }
  }, [initialFeeValue, entryTask?.id, dispatch]);

  useEffect(() => {
    if (formValues?.fees !== undefined) {
      setDisplayedNotaryFee(formValues.fees);
    }
  }, [formValues?.fees]);

  const appointmentInfoWithUpdatedFee = () => {
    if (!entryTask?.appointmentInformation) {
      return [];
    }

    return entryTask.appointmentInformation.map(item => {
      if (item.primary === 'Fee') {
        const numericValue = parseFloat(displayedNotaryFee?.replace(/[^0-9.-]+/g, ''));
        const feeToMoneyFormat = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(isNaN(numericValue) ? 0 : numericValue);

        return { ...item, value: feeToMoneyFormat };
      }
      return item;
    });
  };

  const patchNewEntry = async () => {
    try {
      const newEntry = taskFormValuesToEntry(formValues, currentEntry);
      await notaryAPI.journal.patchJournalEntry(currentEntry.id, newEntry);
      fetchJournalEntry(currentEntry.id, isLocked)(dispatch);
    } catch (error) {
      setJournalError(JOURNAL_ERROR_MESSAGE);
      return false;
    }
    return true;
  };

  const validateAndSubmit = async (submitFn: () => Promise<void>) => {
    if (entryTask?.notaryLocation?.required === true && isEmpty(formValues?.notaryLocation)) {
      setJournalError('Notary Address is required');
      return;
    }
    await submitFn();
  };

  const saveEntry = async () => {
    setIsSubmitting(true);
    try {
      const result = await patchNewEntry();
      if (result) {
        handleClose();
      }
    } catch (err) {
      setJournalError((err as AxiosError<{ message?: string }>).response?.data?.message || JOURNAL_ERROR_MESSAGE);
      setIsSubmitting(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveAndLockEntry = async () => {
    try {
      setIsSubmitting(true);
      await patchNewEntry();
      await notaryAPI.journal.lockJournalEntry(currentEntry.id);
      handleClose();
    } catch (err) {
      setJournalError((err as AxiosError<{ message?: string }>).response?.data?.message || JOURNAL_ERROR_MESSAGE);
      setIsSubmitting(false);
    }
  };

  const downloadEntry = async () => {
    try {
      setIsSubmitting(true);
      await notaryAPI.journal.exportJournalEntry(currentEntry.id);
      handleClose();
    } catch {
      setJournalError(JOURNAL_ERROR_MESSAGE);
      setIsSubmitting(false);
    }
  };

  const formTemplate = !!entryTask ? (
    <>
      {entryTask.title && (
        <h3 data-it="JournalEntry-Title" className={css(styles.title)}>
          {entryTask.title}
        </h3>
      )}
      <h3 className={css(styles.header)}>Appointment Information</h3>

      <InfoCard cardInfo={appointmentInfoWithUpdatedFee()} data-it="JournalEntry-ApptInfo" />
      <h3 className={css(styles.header)}>{entryTask.signerInformation.length > 0 ? 'Signer Information' : null}</h3>
      {isLocked
        ? entryTask.signerInformation.map((signer: any, i: number) => (
            <div
              key={`${signer.display}-${i}-${entryTask.id}`}
              className={css(styles.signerInfoCard)}
              data-it="JournalEntry-SignerInfo"
            >
              <InfoCard cardInfo={signer} />
            </div>
          ))
        : entryTask.signerInputs.map((signer: any, i: number) => (
            <div
              key={`${signer.display}-${i}-${entryTask.id}`}
              className={css(styles.signerInfoCard)}
              data-it="JournalEntry-SignerInput"
            >
              <InputGroup
                key={`${signer.display}-${i}-${entryTask.id}`}
                index={i.toString()}
                display={signer.display}
                imageName={signer.imageName}
                inputs={signer.inputs}
              />
            </div>
          ))}

      {!isLocked && entryTask.notaryLocation && (
        <>
          <TaskInputs
            inputs={[
              {
                id: entryTask.notaryLocation.id,
                type: 'address',
                label: entryTask.notaryLocation.label,
                required: entryTask.notaryLocation.required,
                googleApiKey: entryTask.notaryLocation.googleApiKey,
                display: 'Notary Current Address',
              },
            ]}
          />
        </>
      )}

      {isLocked && entryTask.notaryLocation && (
        <div>
          <h3 className={css(styles.header)}>Notary Information</h3>
          <InfoCard
            cardInfo={[
              {
                primary: 'Address',
                value: dataModelAddressToString(formValues?.notaryLocation),
              },
            ]}
          />
        </div>
      )}

      {!isLocked && (
        <TaskInputs
          inputs={[
            {
              id: 'fees',
              type: 'notary_fee',
              label: 'Notary Fee Amount ($)',
              required: true,
              value: +displayedNotaryFee?.replace('$', ''),
              onChange: (value: number) => {
                setDisplayedNotaryFee(value.toString());
              },
            },
          ]}
        />
      )}

      {isLocked ? (
        <div className={css(styles.notesContainer)}>
          <h3 className={css(styles.header)}>Notes</h3>
          <Card data-it="JournalEntry-NotesInfo">
            <p className={css(styles.noteText)}>{currentEntry?.notes?.value || 'No notes provided'}</p>
          </Card>
        </div>
      ) : (
        <TaskInputs inputs={[entryTask.notesInput]} />
      )}

      <div data-it="JournalEntry-Recordings">
        <h3 className={css(styles.header)}>Appointment Recordings</h3>
        {entryTask.recordings.map(item => (
          <div key={item.id} className={css(styles.attachmentContainer)}>
            <AttachmentCard attachment={item} />
          </div>
        ))}
      </div>

      {/* Documents */}
      <h3 className={css(styles.header)}>Documents</h3>
      {entryTask.documents.length > 0 ? (
        <InfoCard cardInfo={entryTask.documents} data-it="JournalEntry-DocumentsInfo" />
      ) : (
        <Card data-it="JournalEntry-DocumentsInfo">
          <p className={css(styles.noteText)}>No documents provided</p>
        </Card>
      )}
      {/* End of Documents */}

      <div className={css(styles.audioVideoStatementContainer)} data-it="JournalEntry-AudioVideoStatement">
        <p className={css(styles.audioVideoStatement)}>{AUDIO_VIDEO_STATEMENT_CALIFORNIA_REQUIREMENT}</p>
      </div>

      {isLocked ? (
        <ActionButton
          data-dd-privacy="allow"
          disabled={isSubmitting}
          onClick={downloadEntry}
          data-it="ActionButton-Download"
        >
          Download entry
        </ActionButton>
      ) : (
        <>
          {journalError && (
            <Wrapper id={'journal-error'}>
              <Error error={journalError} />
            </Wrapper>
          )}
          <div className={css(styles.saveAndLockButton)}>
            <ActionButton
              data-dd-privacy="allow"
              disabled={isSubmitting}
              onClick={() => validateAndSubmit(saveAndLockEntry)}
              data-it="ActionButton-Lock"
            >
              Save and lock entry using my digital certificate
            </ActionButton>
          </div>
          <ActionButton
            data-dd-privacy="allow"
            disabled={isSubmitting}
            onClick={() => validateAndSubmit(saveEntry)}
            option={'secondary'}
            data-it="ActionButton-Save"
          >
            Save and close
          </ActionButton>
        </>
      )}
    </>
  ) : (
    <div className={css(styles.loadingText)}>Loading...</div>
  );

  return (
    <div className={css(styles.taskWrapper)}>
      <TaskForm onSubmit={noop} formTemplate={formTemplate} />
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    currentEntry: state.entry.current,
    entryTask: state.entry.entryTask,
    isLocked: state.entry.isLocked,
    initialValues: !!state.entry.entryTask ? state.entry.entryTask.initialValues : {},
    formValues: getFormValues(FORM_NAME)(state),
    formErrors: getFormSyncErrors(FORM_NAME)(state),
  };
};

export const JournalEntry = connect<JournalEntry.StateProps, {}, JournalEntry.OwnProps, State>(mapStateToProps)(
  reduxForm<FormValues, JournalEntry.StateProps & JournalEntry.OwnProps>({
    form: FORM_NAME,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    onSubmit: async (_values: FormValues, _dispatch: any, _props: any) => {},
  })(_JournalEntry),
);
